import { LitElement, html, css } from 'lit';
import { API_BASE_URL } from './utils.js';
import { ROUTES } from './routes.js';

export class MarkupStorageTab extends LitElement {
    static properties = {
        contextManager: { type: Object },
        activeContextId: { type: String },
        files: { state: true },
        isLoading: { state: true },
        showTable: { state: true },
        hasLoadedFilesInitially: { state: true },
        contentUrl: { state: true },
        configName: { state: true },
        path: { state: true },
        actionError: { state: true }
    };

    static styles = css`
        :host {
            display: block;
            padding: 20px;
        }

        .files-container {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }

        .button-row {
            display: flex;
            gap: 12px;
            align-items: center;
        }

        .input-row {
            display: flex;
            gap: 12px;
            align-items: center;
        }

        .input-row sp-textfield {
            flex: 1;
        }

        .files-header {
            margin: 0;
        }

        .files-path-input {
            width: 400px;
        }

        sp-table {
            width: 100%;
            max-width: 100%;
        }

        .loading-hidden {
            display: none;
        }

        .table-hidden {
            display: none;
        }

        sp-link {
            color: #0078d4;
            text-decoration: none;
        }

        .no-context-message {
            text-align: center;
            padding: 40px;
            background-color: var(--spectrum-gray-100);
            border-radius: 8px;
            border: 1px solid var(--spectrum-gray-300);
        }

        .action-error {
            color: #ffffff;
            padding: 8px 12px;
            background-color: var(--spectrum-negative-background-color-default, #fff0f1);
            border-radius: 4px;
            border: 1px solid var(--spectrum-negative-border-color-default, #d7373f);
            font-size: 14px;
        }

        sp-table-cell sp-button {
            margin: 0;
        }

        .action-buttons {
            display: flex;
            gap: 8px;
        }

        sp-table sp-table-head-cell:nth-child(2),
        sp-table sp-table-cell:nth-child(2) {
            width: 15%;
            max-width: 100px;
        }
    `;

    constructor() {
        super();
        this.contextManager = null;
        this.activeContextId = null;
        this.files = [];
        this.stateFiles = [];
        this.isLoading = false;
        this.showTable = false;
        this.hasLoadedFilesInitially = false;
        this.contentUrl = '';
        this.configName = 'config';
        this.path = '/';
        this.actionError = '';
    }

    connectedCallback() {
        super.connectedCallback();
        // Ensuring that when the tab becomes visible (e.g. after being hidden)
        // and if it's the first time or context has changed, we load files.
        // This is a more robust way to handle visibility changes if firstUpdated isn't sufficient.
        if (this.activeContextId && !this.hasLoadedFilesInitially) {
            this.handleListFiles();
            this.hasLoadedFilesInitially = true;
        }
    }

    firstUpdated() {
        // Called after the component's DOM has been rendered the first time
        if (this.activeContextId && !this.hasLoadedFilesInitially) {
            this.handleListFiles();
            this.hasLoadedFilesInitially = true;
        }
    }

    updated(changedProperties) {
        if (changedProperties.has('activeContextId')) {
            if (this.activeContextId) {
                // Context has changed to a valid new context, refresh files
                this.handleListFiles();
                this.hasLoadedFilesInitially = true; // Ensure this is set if it wasn't
                this.loadScrapingParameters();
            } else {
                // Context has been cleared, reset state
                this.files = [];
                this.stateFiles = [];
                this.showTable = false;
                this.hasLoadedFilesInitially = false; // Reset for next valid context
                this.contentUrl = '';
                this.configName = 'config';
                this.path = '/';
            }
        }
    }

    loadScrapingParameters() {
        const credentials = this.getActiveCredentials();
        let defaultContentUrl = '';
        if (credentials && credentials.org && credentials.site) {
            defaultContentUrl = `https://main--${credentials.site}--${credentials.org}.aem.live`;
        }

        const storedParams = localStorage.getItem(`scrapingParams_${this.activeContextId}`);
        if (storedParams) {
            const params = JSON.parse(storedParams);
            this.contentUrl = params.contentUrl || defaultContentUrl;
            this.configName = params.configName || 'config';
            this.path = params.path || '/';
        } else {
            this.contentUrl = defaultContentUrl;
            this.configName = 'config';
            this.path = '/';
        }
    }

    saveScrapingParameters() {
        if (this.activeContextId) {
            localStorage.setItem(`scrapingParams_${this.activeContextId}`, JSON.stringify({
                contentUrl: this.contentUrl,
                configName: this.configName,
                path: this.path,
            }));
        }
    }

    handleContentUrlInput(e) {
        this.contentUrl = e.target.value;
        this.saveScrapingParameters();
    }

    handlePathInput(e) {
        this.path = e.target.value;
        this.saveScrapingParameters();
    }

    handleConfigNameInput(e) {
        this.configName = e.target.value;
        this.saveScrapingParameters();
    }

    getActiveCredentials() {
        return this.contextManager?.getContextCredentials();
    }

    async handleListFiles() {
        const credentials = this.getActiveCredentials();
        if (!credentials) {
            console.error('No active context credentials');
            return;
        }

        const { aemAdminToken, aioAuth, aioNamespace } = credentials;

        if (!aemAdminToken || !aioAuth || !aioNamespace) {
            console.error('Missing required credentials');
            return;
        }

        this.isLoading = true;
        this.showTable = false;

        try {
            const url = new URL(`${API_BASE_URL}/files`, window.location.origin);
            if (this.path) {
                url.searchParams.append('path', this.path);
            }
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'X-AEM-admin-token': aemAdminToken,
                    'X-AIO-auth': aioAuth,
                    'X-AIO-namespace': aioNamespace
                }
            });

            const data = await response.json();
            console.log('Files data:', data);
            
            if (data.files && data.files.length > 0) {
                if (data.files[0] && data.files[0].lastModified) {
                    this.files = data.files.sort(
                        (a, b) => new Date(b.lastModified) - new Date(a.lastModified)
                    );
                } else {
                    this.files = data.files;
                }
            } else {
                this.files = [];
            }

            const stateFilePaths = ['check-product-changes/', 'render-all-categories/'];
            const stateHeaders = {
                'X-AEM-admin-token': aemAdminToken,
                'X-AIO-auth': aioAuth,
                'X-AIO-namespace': aioNamespace
            };

            const stateFileResponses = await Promise.all(stateFilePaths.map(path => {
                url.searchParams.set('path', path);
                return fetch(url, { method: 'GET', headers: stateHeaders }).then(r => r.json());
            }));

            const allStateFiles = stateFileResponses.flatMap(data => data.files || []);
            if (allStateFiles.length > 0) {
                if (allStateFiles[0]?.lastModified) {
                    this.stateFiles = allStateFiles.sort(
                        (a, b) => new Date(b.lastModified) - new Date(a.lastModified)
                    );
                } else {
                    this.stateFiles = allStateFiles;
                }
            } else {
                this.stateFiles = [];
            }
            
            this.showTable = true;
        } catch (error) {
            console.error('Error fetching files:', error);
            this.files = [];
            this.stateFiles = [];
        } finally {
            this.isLoading = false;
        }
    }

    async handleResetFilesList() {
        const credentials = this.getActiveCredentials();
        if (!credentials) {
            console.error('No active context credentials');
            return;
        }

        const { aemAdminToken, aioAuth, aioNamespace } = credentials;

        if (!aemAdminToken || !aioAuth || !aioNamespace) {
            console.error('Missing required credentials');
            return;
        }

        this.isLoading = true;
        this.showTable = false;

        try {
            const response = await fetch(`${API_BASE_URL}/change-detector/state`, {
                method: 'DELETE',
                headers: {
                    'X-AEM-admin-token': aemAdminToken,
                    'X-AIO-auth': aioAuth,
                    'X-AIO-namespace': aioNamespace
                }
            });

            if (response.ok) {
                const result = await response.json();
                if (result.hasMoreFiles) {
                    setTimeout(() => this.handleResetFilesList(), 1000);
                    return;
                }
            }

            // Refresh the file list
            await this.handleListFiles();
        } catch (error) {
            console.error('Error resetting markup storage files list:', error);
        } finally {
            this.isLoading = false;
        }
    }

    async handleTriggerProductScraping() {
        const credentials = this.getActiveCredentials();
        if (!credentials) {
            console.error('No active context credentials');
            return;
        }

        const { aemAdminToken, aioAuth, aioNamespace } = credentials;

        if (!aemAdminToken || !aioAuth || !aioNamespace) {
            console.error('Missing required credentials or project info');
            return;
        }

        this.isLoading = true;
        this.actionError = '';

        try {
            const response = await fetch(`${API_BASE_URL}/product-scraper/scrape`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-AEM-admin-token': aemAdminToken,
                    'X-AIO-auth': aioAuth,
                    'X-AIO-namespace': aioNamespace
                },
                body: JSON.stringify({
                    contentUrl: this.contentUrl,
                    configName: this.configName
                })
            });

            const data = await response.json();
            console.log('Product scraping response:', data);

            if (!response.ok) {
                this.actionError = data.error || 'An unexpected error occurred.';
            } else {
                await this.handleListFiles();
            }
        } catch (error) {
            console.error('Error triggering product scraping:', error);
        } finally {
            this.isLoading = false;
        }
    }

    async handleTriggerRenderAllCategories() {
        const credentials = this.getActiveCredentials();
        if (!credentials) {
            console.error('No active context credentials');
            return;
        }

        const { aemAdminToken, aioAuth, aioNamespace } = credentials;

        if (!aemAdminToken || !aioAuth || !aioNamespace) {
            console.error('Missing required credentials or project info');
            return;
        }

        this.isLoading = true;
        this.actionError = '';

        try {
            const response = await fetch(`${API_BASE_URL}/categories/render`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-AEM-admin-token': aemAdminToken,
                    'X-AIO-auth': aioAuth,
                    'X-AIO-namespace': aioNamespace
                },
                body: JSON.stringify({
                    contentUrl: this.contentUrl,
                    configName: this.configName
                })
            });

            const data = await response.json();
            console.log('Render all categories response:', data);

            if (!response.ok) {
                this.actionError = data.error || 'An unexpected error occurred.';
            } else {
                await this.handleListFiles();
            }
        } catch (error) {
            console.error('Error triggering render all categories:', error);
        } finally {
            this.isLoading = false;
        }
    }

    formatFileSize(bytes) {
        return `${(bytes / 1024).toFixed(2)} KB`;
    }

    formatDate(dateString) {
        return new Date(dateString).toLocaleString();
    }

    // Jumps to the Renderer tab with the path pre-filled so the stored
    // markup can be re-rendered live. Whether it's a PDP or PLP isn't
    // knowable from the path alone, so the Renderer tab's own PDP/PLP
    // toggle (defaulting to PDP) is left for the user to set.
    handlePreviewClick(event, file) {
        event.preventDefault();
        let path = file.name;
        const prefix = 'public/pdps/';
        if (path.startsWith(prefix)) {
            path = path.substring(prefix.length);
        }
        const uri = `/${path.replace(/\.html$/, '')}`;

        const params = new URLSearchParams();
        params.set('path', uri);
        if (this.activeContextId) {
            params.set('contextId', this.activeContextId);
        }
        window.location.hash = `#/${ROUTES.RENDERER}?v=1&${params.toString()}`;
    }

    openTextInNewTab(variable) {
        let textContent = '';
        try{
            textContent = JSON.parse(variable);
            textContent = JSON.stringify(textContent, null, 2);
        } catch (error) {
            textContent = variable;
        } finally {
            const newTab = window.open('', '_blank');
            newTab.document.body.innerHTML = `<pre>${textContent}</pre>`;
            newTab.document.close()

        }
    }

    // used to retrieve state files that are private
    async handleGetFile(filename) {
    
        const credentials = this.getActiveCredentials();
        if (!credentials) {
            console.error('No active context credentials');
            return;
        }

        const { aemAdminToken, aioAuth, aioNamespace } = credentials;

        if (!aemAdminToken || !aioAuth || !aioNamespace) {
            console.error('Missing required credentials');
            return;
        }

        this.isLoading = true;
        this.showTable = false;

        try {
            const url = new URL(`${API_BASE_URL}/file`, window.location.origin);
            url.searchParams.append('file', filename);
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'X-AEM-admin-token': aemAdminToken,
                    'X-AIO-auth': aioAuth,
                    'X-AIO-namespace': aioNamespace
                }
            });

            const data = await response.json();
            this.openTextInNewTab(data.return);
            this.showTable = true;
        } catch (error) {
            console.error('Error fetching files:', error);
            this.files = [];
        } finally {
            this.isLoading = false;
        }
            
    }

    async handleDeleteFile(file) {
        const credentials = this.getActiveCredentials();
        if (!credentials) {
            console.error('No active context credentials');
            return;
        }

        const { aemAdminToken, aioAuth, aioNamespace } = credentials;
        if (!aemAdminToken || !aioAuth || !aioNamespace) {
            console.error('Missing required credentials');
            return;
        }

        try {
            const cleanResp = await fetch(`${API_BASE_URL}/statefile-clean`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-AEM-admin-token': aemAdminToken,
                    'X-AIO-auth': aioAuth,
                    'X-AIO-namespace': aioNamespace
                },
                body: JSON.stringify({
                    filename: file.name
                })
            });
            const cleanData = await cleanResp.json();
            
            if (!cleanResp.ok) {
                console.error('Failed to extract SKU and clean CSV:', cleanData);
                alert(`Failed to process: ${cleanData.message || 'Unknown error'}`);
                return;
            }
            
            const extractedSku = cleanData.extractedSku;
            
            if (!extractedSku) {
                alert('No SKU found in markup file. Cannot proceed with deletion.');
                return;
            }
            
            const deleteResp = await fetch(`${API_BASE_URL}/markup-delete`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'X-AEM-admin-token': aemAdminToken,
                    'X-AIO-auth': aioAuth,
                    'X-AIO-namespace': aioNamespace
                },
                body: JSON.stringify({
                    filename: file.name
                })
            });

            const deleteData = await deleteResp.json();
            if (deleteResp.ok) {
                await this.handleListFiles();
            } else {
                console.error('Error during markup delete:', deleteData);
                alert(`Failed to delete markup file: ${deleteData.error || deleteData.message || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Error deleting file:', error);
            alert(`Failed to delete file: ${error.message}`);
        }
    }

    renderFileRow(file) {
        let fileLink = file.url;
        let fileCell = html`<sp-link href="${fileLink}" target="_blank" rel="noopener noreferrer">${file.name}</sp-link>`
        // handle private files
        if (!file.isPublic) {
            fileCell = html`<sp-link @click="${()=>this.handleGetFile(file.name)}" target="_blank">${file.name}</sp-link>`
        }

        return html`
            <sp-table-row>
                <sp-table-cell>${fileCell}</sp-table-cell>
                <sp-table-cell>${this.formatFileSize(file.contentLength)}</sp-table-cell>                
                <sp-table-cell>${this.formatDate(file.creationTime)}</sp-table-cell>                
                <sp-table-cell>${this.formatDate(file.lastModified)}</sp-table-cell>
                <sp-table-cell>
                    <div class="action-buttons">
                        ${file.isPublic ?
                            html`<sp-button
                            size="s"
                            variant="secondary"
                            @click="${(e) => this.handlePreviewClick(e, file)}"
                            title="Preview this file in the Renderer tab">
                            Preview
                            </sp-button>` :
                            ''
                        }
                        ${file.name.startsWith('/public/') || file.name.startsWith('public/') ?
                            html`<sp-button
                            size="s"
                            variant="negative"
                            @click="${() => this.handleDeleteFile(file)}"
                            title="Delete this file">
                            Delete Markup
                            </sp-button>` :
                            ''
                        }
                    </div>
                </sp-table-cell>
            </sp-table-row>
        `;
    }

    render() {
        if (!this.activeContextId) {
            return html`
                <div class="no-context-message">
                    <p>Please select a project context to see the generated markup files.</p>
                </div>
            `;
        }

        return html`
            <div class="files-container">
                <div class="button-row">
                    <sp-button variant="primary" @click="${this.handleListFiles}">Refresh</sp-button>
                    <sp-button variant="negative" @click="${this.handleResetFilesList}">Reset Markup Storage</sp-button>
                    <sp-button 
                        variant="primary" 
                        @click="${this.handleTriggerProductScraping}"
                        ?disabled="${this.isLoading}">
                        Trigger Product Scraping
                    </sp-button>
                    <sp-button 
                        variant="primary" 
                        @click="${this.handleTriggerRenderAllCategories}"
                        ?disabled="${this.isLoading}">
                        Trigger Render All Categories
                    </sp-button>
                    <sp-progress-circle 
                        label="Loading file list"
                        indeterminate
                        class="${this.isLoading ? '' : 'loading-hidden'}">
                    </sp-progress-circle>
                </div>
                ${this.actionError ? html`<div class="action-error">${this.actionError}</div>` : ''}
                <div class="input-row">
                    <sp-textfield
                        class="files-path-input"
                        placeholder="Enter path to list files from e.g. /public/pdps"
                        .value="${this.path}"
                        @input="${this.handlePathInput}"
                    ></sp-textfield>
                </div>
                <div class="input-row">
                    <sp-textfield
                        placeholder="Enter Content URL (e.g., https://main--site--org.aem.live)"
                        .value="${this.contentUrl}"
                        @input="${this.handleContentUrlInput}">
                    </sp-textfield>
                    <sp-textfield
                        placeholder="Config Name"
                        .value="${this.configName}"
                        @input="${this.handleConfigNameInput}">
                    </sp-textfield>
                </div>
                <h3 class="files-header">State Files (${this.stateFiles.length})</h3>
                <div class="table-container ${this.showTable ? '' : 'table-hidden'}">
                    <sp-table>
                        <sp-table-head>
                            <sp-table-head-cell>File</sp-table-head-cell>
                            <sp-table-head-cell>File Size</sp-table-head-cell>
                            <sp-table-head-cell>Created Date</sp-table-head-cell>
                            <sp-table-head-cell>Last Modified Date</sp-table-head-cell>
                            <sp-table-head-cell>Actions</sp-table-head-cell>
                        </sp-table-head>
                        <sp-table-body>
                            ${this.stateFiles.map(file => this.renderFileRow(file))}
                        </sp-table-body>
                    </sp-table>
                </div>
                <h3 class="files-header">Generated Markup Files (${this.files.length})</h3>
                <div class="table-container ${this.showTable ? '' : 'table-hidden'}">
                    <sp-table>
                        <sp-table-head>
                            <sp-table-head-cell>File</sp-table-head-cell>
                            <sp-table-head-cell>File Size</sp-table-head-cell>
                            <sp-table-head-cell>Created Date</sp-table-head-cell>
                            <sp-table-head-cell>Last Modified Date</sp-table-head-cell>
                            <sp-table-head-cell>Actions</sp-table-head-cell>
                        </sp-table-head>
                        <sp-table-body>
                            ${this.files.map(file => this.renderFileRow(file))}
                        </sp-table-body>
                    </sp-table>
                </div>
            </div>
        `;
    }
}

customElements.define('markup-storage-tab', MarkupStorageTab); 